import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import * as authApi from '../api/auth'
import { ApiError } from '../api/auth'
import type { AuthUser, LoginResult, RoleKey } from '../types/identity'

export interface AuthState {
  user: AuthUser | null
  /** True while the initial `/me` probe is running. */
  loading: boolean
  /** Set when the signed-in manager still has to enrol 2FA (D-21). */
  twoFactorRequired: boolean
  signIn: (
    email: string,
    password: string,
    rememberDevice?: boolean,
  ) => Promise<{ mfaRequired: boolean; ticket: string | null }>
  completeTwoFactor: (ticket: string, code: string, rememberDevice?: boolean) => Promise<void>
  signOut: () => Promise<void>
  refresh: () => Promise<void>
  /** Called after 2FA enrolment so the app can leave the “enrol first” state. */
  markTwoFactorSatisfied: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [twoFactorRequired, setTwoFactorRequired] = useState(false)

  const loadUser = useCallback(async () => {
    try {
      const me = await authApi.fetchMe()
      setUser(me)
      setTwoFactorRequired(false)
    } catch (error) {
      setUser(null)
      if (error instanceof ApiError && error.code === '2fa_required') {
        // Signed in, but the manager policy wants 2FA before anything else.
        setTwoFactorRequired(true)
        try {
          const me = await authApi.fetchMe()
          setUser(me)
        } catch {
          setUser(null)
        }
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadUser()
  }, [loadUser])

  const signIn = useCallback(
    async (email: string, password: string, rememberDevice = false) => {
      const result: LoginResult = await authApi.login(email, password, rememberDevice)
      if (result.mfa_required) {
        return { mfaRequired: true, ticket: result.mfa_ticket }
      }
      setUser(result.user)
      setTwoFactorRequired(false)
      return { mfaRequired: false, ticket: null }
    },
    [],
  )

  const completeTwoFactor = useCallback(
    async (ticket: string, code: string, rememberDevice = false) => {
      const result = await authApi.loginWithTwoFactor(ticket, { code }, rememberDevice)
      setUser(result.user)
      setTwoFactorRequired(false)
    },
    [],
  )

  const signOut = useCallback(async () => {
    try {
      await authApi.logout()
    } finally {
      setUser(null)
      setTwoFactorRequired(false)
    }
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      twoFactorRequired,
      signIn,
      completeTwoFactor,
      signOut,
      refresh: loadUser,
      markTwoFactorSatisfied: loadUser,
    }),
    [user, loading, twoFactorRequired, signIn, completeTwoFactor, signOut, loadUser],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used inside <AuthProvider>')
  }
  return context
}

/**
 * The backend owns the capability matrix. UI code asks this helper — it must never
 * infer access from a role name, or the two matrices will drift again.
 */
export function hasCapability(user: AuthUser | null, capability: string): boolean {
  if (!user) return false
  return user.capabilities?.includes(capability) ?? false
}

export function canAdministerAccounts(user: AuthUser | null): boolean {
  return hasCapability(user, 'accounts.manage')
}

/** True when the signed-in person still has to pair an authenticator app. */
export function requiresTwoFactor(user: AuthUser | null): boolean {
  if (!user) return false
  // Already paired: never gate again. The server tells us whether the policy
  // applies; assistant managers are not in the mandatory list (D-35).
  if (user.two_factor_enabled) return false
  return Boolean(user.two_factor_required)
}

/** Roles an account may grant: strictly below their own rank (§13.7). */
export function grantableRoles(user: AuthUser | null): RoleKey[] {
  return user?.grantable_roles ?? []
}
